import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { uxMessagingApi } from "./api";
import { queryClient } from "@shared/api/queries/queryClient";
import { toast } from "@shared/hooks/use-toast";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { logEvent } from "@shared/analytics/analytics";

export const usePostDirectMessage = () =>
  useSmartMutation(uxMessagingApi.postDirectMessage, {
    onSuccess: (data, variables) => {
      let message = "Message sent successfully!";

      if (variables.scheduled_date) {
        const scheduledDateTime = new Date(variables.scheduled_date);
        const formattedDate = scheduledDateTime.toLocaleDateString();
        const formattedTime = scheduledDateTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        message = `Message scheduled for ${formattedDate} at ${formattedTime}`;
      }

      toast({
        title: "Success",
        description: message,
      });

      queryClient.invalidateQueries({
        queryKey: ["ux", "notification-history"],
      });
    },
    onError: (error) => {
      toast({description: error.message});
      logEvent('Individual message error while sending', {
        error: error.message
      })
    }
  });

export const useRecipientLists = ({
  recipientType,
  remoteUtilityId,
}: {
  recipientType: string;
  remoteUtilityId: string;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.ux.messaging.recipientLists(recipientType),
    () => uxMessagingApi.getRecipientLists(recipientType, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!recipientType,
    }
  );

export const useTemplates = ({
  channelType,
  remoteUtilityId,
}: {
  channelType: string;
  remoteUtilityId: string;
}) =>
  useSmartQuery(
    QueryKeyFactory.module.ux.messaging.templates(channelType),
    () => uxMessagingApi.getTemplates(channelType, remoteUtilityId),
    {
      enabled: !!remoteUtilityId && !!channelType,
    }
  );

export const usePostBulkNotification = () =>
  useSmartMutation(uxMessagingApi.postBulkNotification, {
    onSuccess: (data, variables) => {
      let message = "Bulk notification sent successfully!";

      if (variables.scheduled_date) {
        const scheduledDateTime = new Date(variables.scheduled_date);
        const formattedDate = scheduledDateTime.toLocaleDateString();
        const formattedTime = scheduledDateTime.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        });
        message = `Bulk notification scheduled for ${formattedDate} at ${formattedTime}`;
      }

      toast({
        title: "Success",
        description: message,
      });
    },
    onError: (error: Error) => {
      toast({ description: error.message });
      logEvent('Bulk Message Error While Sending', {
        error: error.message
      })
    },
  });
